import { useState } from "react";
import { Download, ExternalLink, Eye, FileText } from "lucide-react";
import { fileUrl } from "../lib/api";
import { Button } from "./ui/button";
import { ImageLightbox } from "./ImageLightbox";

const fmtSize = (n) => {
  if (n == null) return "—";
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("es-MX") : "—");

export const ReceiptViewer = ({ receipt, versions = [], canDownload = false, title = "Comprobante de pago", testId = "receipt-viewer" }) => {
  const [open, setOpen] = useState(false);

  if (!receipt || !receipt.download_url) {
    return (
      <p className="rounded-lg bg-hz-bg px-3 py-2 text-xs text-hz-ink/60" data-testid={`${testId}-empty`}>
        Sin comprobante registrado.
      </p>
    );
  }

  const isImage = (receipt.mime_type || "").startsWith("image/");
  const url = fileUrl(receipt.download_url);

  return (
    <div className="rounded-xl border border-hz-gray/40 bg-white" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
        <FileText className="h-5 w-5 flex-none text-hz-navy" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-hz-navy" data-testid={`${testId}-filename`}>
            {receipt.original_filename || "comprobante"}
          </p>
          <p className="text-xs text-hz-ink/60" data-testid={`${testId}-meta`}>
            {receipt.mime_type || "archivo"} · {fmtSize(receipt.file_size)} · v{receipt.version_number} · {fmtDate(receipt.uploaded_at)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isImage ? (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`${testId}-view-btn`} className="text-xs font-bold">
              <Eye className="mr-1 h-3.5 w-3.5" /> Ver comprobante
            </Button>
          ) : (
            <a href={url} target="_blank" rel="noreferrer" data-testid={`${testId}-view-btn`}
              className="inline-flex items-center gap-1 rounded-md border border-hz-gray/50 px-3 py-1.5 text-xs font-bold text-hz-navy hover:border-hz-cyan">
              <Eye className="h-3.5 w-3.5" /> Ver comprobante (PDF)
            </a>
          )}
          {isImage && (
            <a href={url} target="_blank" rel="noreferrer" data-testid={`${testId}-open-tab`}
              className="inline-flex items-center gap-1 rounded-md border border-hz-gray/50 px-2 py-1.5 text-xs font-bold text-hz-navy hover:border-hz-cyan"
              aria-label="Abrir comprobante en nueva pestaña">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          {canDownload && (
            <a href={url} download={receipt.original_filename || true} data-testid={`${testId}-download`}
              className="inline-flex items-center gap-1 rounded-md border border-hz-gray/50 px-2 py-1.5 text-xs font-bold text-hz-navy hover:border-hz-cyan"
              aria-label="Descargar comprobante">
              <Download className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>
      {versions.length > 1 && (
        <div className="border-t border-hz-gray/30 px-3 py-2" data-testid={`${testId}-versions`}>
          <p className="text-xs font-bold text-hz-navy">Historial de versiones</p>
          <ul className="mt-1 space-y-1">
            {versions.map((v) => (
              <li key={v.id || `v${v.version_number}`} className="flex flex-wrap items-center justify-between gap-2 text-xs" data-testid={`${testId}-version-${v.version_number}`}>
                <span className="font-semibold">
                  v{v.version_number} · {v.status === "active" ? "Activa" : v.status === "superseded" ? "Reemplazada" : v.status === "rejected" ? "Rechazada" : v.status}
                  {v.source ? ` · ${v.source}` : ""}
                </span>
                <span className="text-hz-ink/60">{fmtDate(v.uploaded_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {open && isImage && (
        <ImageLightbox src={url} alt={title} title={title} onClose={() => setOpen(false)} testId={`${testId}-lightbox`} />
      )}
    </div>
  );
};
